import React, { useEffect, useRef } from 'react';
import p5 from 'p5';

interface Particle {
  x: number;
  y: number;
  size: number;
  vx: number;
  vy: number;
  opacity: number;
  rotation: number;
  rotationSpeed: number;
  color: p5.Color;
}

export const P5JSAnimation: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const p5InstanceRef = useRef<p5 | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const sketch = (p: p5) => {
      let particles: Particle[] = [];
      let hooverX: number;
      let hooverY: number;
      let hooverHeadX: number;
      let hooverHeadY: number;
      let oscillationTime = 0;
      let vacuumImg: p5.Image | null = null;
      
      // Litter colors (browns, grays, yellows for trash-like appearance)
      const litterColors = [
        [114, 65, 18],     // Brown (original: 139, 90, 43)
        [135, 57, 20],     // Sienna (original: 160, 82, 45)
        [185, 155, 115],   // Tan (original: 210, 180, 140)
        [144, 144, 144],   // Gray (original: 169, 169, 169)
        [103, 103, 103],   // Dark gray (original: 128, 128, 128)
        [230, 203, 156],   // Moccasin (original: 255, 228, 181)
        [220, 197, 154]    // Wheat (original: 245, 222, 179)
      ];

      p.setup = () => {
        const canvas = p.createCanvas(p.windowWidth, p.windowHeight);
        canvas.parent(containerRef.current!);
        
        // Load vacuum cleaner image with callback
        p.loadImage('/vacuum-cleaner.png', (img) => {
          vacuumImg = img;
        });
        
        // Initialize hoover position (top-left area)
        hooverX = p.width * 0.15;
        hooverY = -20;
        
        // Initialize particles
        initParticles();
      };

      const initParticles = () => {
        particles = [];
        const numParticles = 100;
        
        for (let i = 0; i < numParticles; i++) {
          let x, y;
          
          // Spawn particles from all edges (bottom 50% of perimeter)
          const edge = p.random(1);
          
          if (edge < 0.25) {
            // Left edge (bottom 50%)
            x = p.random(p.width * 0.05);
            y = p.height * 0.5 + p.random(p.height * 0.5);
          } else if (edge < 0.5) {
            // Right edge (bottom 50%)
            x = p.width * 0.95 + p.random(p.width * 0.05);
            y = p.height * 0.5 + p.random(p.height * 0.5);
          } else if (edge < 0.75) {
            // Bottom edge (full width)
            x = p.random(p.width);
            y = p.height * 0.9 + p.random(p.height * 0.1);
          } else {
            // Top edge (outer sides only, avoid hoover area)
            const side = p.random(1);
            if (side < 0.5) {
              x = p.random(p.width * 0.4); // Left side of top
            } else {
              x = p.width * 0.6 + p.random(p.width * 0.4); // Right side of top
            }
            y = p.random(p.height * 0.3);
          }
          
          const colorIndex = Math.floor(p.random(litterColors.length));
          const baseColor = litterColors[colorIndex];
          
          particles.push({
            x,
            y,
            size: p.random(8, 15), // Varying sizes for litter pieces
            vx: 0,
            vy: 0,
            opacity: 255,
            rotation: p.random(p.TWO_PI),
            rotationSpeed: p.random(-0.05, 0.05),
            color: p.color(baseColor[0], baseColor[1], baseColor[2]),
          });
        }
      };

      p.draw = () => {
        p.clear();
        p.background(0, 0, 0, 0); // Transparent background
        
        // Update oscillation time
        oscillationTime += 0.01;
        
        // Calculate hoover position with horizontal oscillation
        const oscillation = p.sin(oscillationTime) * p.width * 0.1;
        hooverX = p.width * 0.15 + oscillation;
        
        // Hoover head position (bottom right of the vacuum, 20px from edge)
        const imgWidth = 112.5;
        const imgHeight = 112.5;
        hooverHeadX = hooverX + imgWidth - 20;
        hooverHeadY = hooverY + imgHeight - 20;
        
        // Update and draw particles
        updateParticles();
        drawParticles();
        
        // Draw hoover
        drawHoover();
        
        // Draw suction effect
        drawSuctionEffect();
      };

      const updateParticles = () => {
        particles.forEach((particle, index) => {
          // Calculate direction to hoover head
          const dx = hooverHeadX - particle.x;
          const dy = hooverHeadY - particle.y;
          const distance = p.dist(particle.x, particle.y, hooverHeadX, hooverHeadY);
          
          if (distance > 20) {
            // Apply suction force (stronger when closer) - 2x speed
            const force = 0.1 + (1 / (distance + 1)) * 1.0;
            particle.vx += (dx / distance) * force;
            particle.vy += (dy / distance) * force;
            
            // Apply drag - less drag for faster movement
            particle.vx *= 0.94;
            particle.vy *= 0.94;
            
            // Update position
            particle.x += particle.vx;
            particle.y += particle.vy;
            
            // Update rotation
            particle.rotation += particle.rotationSpeed;
            
            // Fade out as it gets closer
            if (distance < 100) {
              particle.opacity = p.map(distance, 0, 100, 28, 255);
            } else {
              particle.opacity = 255;
            }
          } else {
            // Particle reached hoover - respawn from edges
            const edge = p.random(1);
            
            if (edge < 0.25) {
              // Left edge (bottom 50%)
              particle.x = p.random(p.width * 0.05);
              particle.y = p.height * 0.5 + p.random(p.height * 0.5);
            } else if (edge < 0.5) {
              // Right edge (bottom 50%)
              particle.x = p.width * 0.95 + p.random(p.width * 0.05);
              particle.y = p.height * 0.5 + p.random(p.height * 0.5);
            } else if (edge < 0.75) {
              // Bottom edge (full width)
              particle.x = p.random(p.width);
              particle.y = p.height * 0.9 + p.random(p.height * 0.1);
            } else {
              // Top edge (outer sides only, avoid hoover area)
              const side = p.random(1);
              if (side < 0.5) {
                particle.x = p.random(p.width * 0.4); // Left side of top
              } else {
                particle.x = p.width * 0.6 + p.random(p.width * 0.4); // Right side of top
              }
              particle.y = p.random(p.height * 0.3);
            }
            
            particle.vx = 0;
            particle.vy = 0;
            particle.opacity = 255;
            particle.rotation = p.random(p.TWO_PI);
            
            // Randomize color again
            const colorIndex = Math.floor(p.random(litterColors.length));
            const baseColor = litterColors[colorIndex];
            particle.color = p.color(baseColor[0], baseColor[1], baseColor[2]);
          }
        });
      };

      const drawParticles = () => {
        particles.forEach((particle) => {
          p.push();
          p.translate(particle.x, particle.y);
          p.rotate(particle.rotation);
          
          // Set stroke color with opacity (no fill)
          const c = particle.color;
          p.noFill();
          p.stroke(p.red(c), p.green(c), p.blue(c), particle.opacity);
          p.strokeWeight(1);
          
          // Draw organic, wavy, distorted circular shapes
          p.beginShape();
          const numPoints = 12 + Math.floor(particle.size / 3);
          const baseRadius = particle.size / 2;
          
          for (let i = 0; i < numPoints; i++) {
            const angle = (p.TWO_PI / numPoints) * i;
            
            const wave1 = p.sin(angle * 3 + particle.rotation * 2) * 0.2;
            const wave2 = p.sin(angle * 5 - particle.rotation) * 0.15;
            const wave3 = p.cos(angle * 7 + particle.rotation * 0.5) * 0.1;
            
            const distortion = 1 + wave1 + wave2 + wave3;
            const r = baseRadius * distortion;
            
            const x = r * p.cos(angle);
            const y = r * p.sin(angle);
            
            p.vertex(x, y);
          }
          p.endShape(p.CLOSE);
          
          p.pop();
        });
      };

      const drawHoover = () => {
        if (!vacuumImg) return;
        
        p.push();
        p.translate(hooverX, hooverY);
        
        // Draw vacuum cleaner PNG image
        // Scale to appropriate size (75% of original 150x150)
        const imgWidth = 112.5;
        const imgHeight = 112.5;
        
        p.image(vacuumImg, 0, 0, imgWidth, imgHeight);
        
        p.pop();
      };

      const drawSuctionEffect = () => {
        // Draw concentric circles emanating from suction point
        const numCircles = 5;
        
        for (let i = 0; i < numCircles; i++) {
          p.push();
          
          // Stagger the animation timing for each circle
          const circleOffset = i * 0.5;
          const circleProgress = ((oscillationTime * 2 + circleOffset) % 3) / 3;
          
          // Calculate radius based on progress
          const radius = circleProgress * 150;
          
          // Fade out as circle expands
          const opacity = (1 - circleProgress) * 150;
          
          // Color gradient from light grey to grey based on progress
          const greyValue = p.map(circleProgress, 0, 1, 200, 100);
          
          if (opacity > 10 && radius > 5) {
            p.noFill();
            p.stroke(greyValue, greyValue, greyValue, opacity);
            p.strokeWeight(2);
            
            // Draw circle at suction point
            p.ellipse(hooverHeadX, hooverHeadY, radius * 2, radius * 2);
          }
          
          p.pop();
        }
      };

      p.windowResized = () => {
        p.resizeCanvas(p.windowWidth, p.windowHeight);
        initParticles();
      };
    };

    // Create p5 instance
    p5InstanceRef.current = new p5(sketch);

    // Cleanup
    return () => {
      if (p5InstanceRef.current) {
        p5InstanceRef.current.remove();
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
        opacity: 0.3,
        zIndex: 1,
      }}
    />
  );
};

export default P5JSAnimation;
